const CONVERTIBLE_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/gif',
]);

const CONVERTIBLE_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'bmp', 'gif']);
const JPEG_QUALITY = 0.92;

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageCrop extends ImageDimensions {
  x: number;
  y: number;
}

export type ImageColorSpace = 'srgb' | 'unknown';

export interface ConvertedImage extends ImageDimensions {
  file: File;
  colorSpace: ImageColorSpace;
}

export const canConvertImageToJpeg = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return CONVERTIBLE_IMAGE_MIME_TYPES.has(mimeType) || CONVERTIBLE_IMAGE_EXTENSIONS.has(extension);
};

const hasValidDimensions = (width: number, height: number): boolean =>
  Number.isSafeInteger(width) && Number.isSafeInteger(height) && width > 0 && height > 0;

const hasJpegSignature = async (file: File): Promise<boolean> => {
  const signature = new Uint8Array(await file.slice(0, 3).arrayBuffer());
  return signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff;
};

interface AspectRatioRange {
  minimum: number;
  maximum: number;
}

interface LinkedDimensionOptions {
  value: number;
  maximum?: ImageDimensions;
}

export const calculateResizeDimensions = (
  source: ImageDimensions,
  requested: ImageDimensions,
  maximum: ImageDimensions = source,
): ImageDimensions => {
  if (
    !hasValidDimensions(source.width, source.height) ||
    !hasValidDimensions(requested.width, requested.height) ||
    !hasValidDimensions(maximum.width, maximum.height) ||
    requested.width > maximum.width ||
    requested.height > maximum.height
  ) {
    throw new RangeError('Resize dimensions must be positive integers within the configured limits');
  }

  return requested;
};

export const calculateLinkedDimension = (
  original: ImageDimensions,
  dimension: 'width' | 'height',
  { value, maximum = original }: LinkedDimensionOptions,
): ImageDimensions => {
  if (!hasValidDimensions(original.width, original.height) || !Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError('Image dimensions must be positive integers');
  }

  const aspectRatio = original.width / original.height;
  const maximumValue = dimension === 'width'
    ? Math.min(maximum.width, Math.floor(maximum.height * aspectRatio))
    : Math.min(maximum.height, Math.floor(maximum.width / aspectRatio));
  const boundedValue = Math.min(value, maximumValue);
  const requested = dimension === 'width'
    ? { width: boundedValue, height: Math.max(1, Math.round(boundedValue / aspectRatio)) }
    : { width: Math.max(1, Math.round(boundedValue * aspectRatio)), height: boundedValue };

  return calculateResizeDimensions(original, requested, maximum);
};

export const calculateCenterCrop = (
  source: ImageDimensions,
  aspectRatioRange: AspectRatioRange,
): ImageCrop => {
  const { minimum, maximum } = aspectRatioRange;
  if (
    !hasValidDimensions(source.width, source.height) ||
    !Number.isFinite(minimum) ||
    !Number.isFinite(maximum) ||
    minimum <= 0 ||
    maximum < minimum
  ) {
    throw new RangeError('Image dimensions and aspect-ratio bounds must be valid');
  }

  const aspectRatio = source.width / source.height;
  if (aspectRatio > maximum) {
    const width = Math.floor(source.height * maximum);
    return { x: Math.floor((source.width - width) / 2), y: 0, width, height: source.height };
  }
  if (aspectRatio < minimum) {
    const height = Math.floor(source.width / minimum);
    return { x: 0, y: Math.floor((source.height - height) / 2), width: source.width, height };
  }

  return { x: 0, y: 0, width: source.width, height: source.height };
};

const loadImage = async (file: File): Promise<HTMLImageElement> => {
  const sourceUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('The selected file could not be decoded as an image'));
      image.src = sourceUrl;
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const isValidCrop = (crop: ImageCrop, width: number, height: number): boolean =>
  hasValidDimensions(crop.width, crop.height) &&
  Number.isSafeInteger(crop.x) &&
  Number.isSafeInteger(crop.y) &&
  crop.x >= 0 &&
  crop.y >= 0 &&
  crop.x + crop.width <= width &&
  crop.y + crop.height <= height;

export const convertImageToJpeg = async (
  file: File,
  requestedDimensions?: ImageDimensions,
  crop?: ImageCrop,
): Promise<ConvertedImage> => {
  if (!canConvertImageToJpeg(file)) {
    throw new Error('The selected image format is not supported for conversion');
  }

  const image = await loadImage(file);
  if (crop && !isValidCrop(crop, image.naturalWidth, image.naturalHeight)) {
    throw new RangeError('Crop dimensions must fit inside the source image');
  }

  const sourceDimensions = { width: crop?.width ?? image.naturalWidth, height: crop?.height ?? image.naturalHeight };
  const dimensions = calculateResizeDimensions(
    sourceDimensions,
    requestedDimensions ?? sourceDimensions,
    requestedDimensions ?? sourceDimensions,
  );
  const { width: sourceWidth, height: sourceHeight } = sourceDimensions;
  const hasJpegMimeType = file.type.toLowerCase() === 'image/jpeg';
  const hasJpegEncoding = hasJpegMimeType && await hasJpegSignature(file);
  if (
    hasJpegEncoding &&
    !crop &&
    dimensions.width === image.naturalWidth &&
    dimensions.height === image.naturalHeight
  ) {
    return { file, ...dimensions, colorSpace: 'unknown' };
  }

  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;

  const context = canvas.getContext('2d', { colorSpace: 'srgb' });
  if (!context) {
    throw new Error('Could not initialize image conversion');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, dimensions.width, dimensions.height);
  context.drawImage(
    image,
    crop?.x ?? 0,
    crop?.y ?? 0,
    sourceWidth,
    sourceHeight,
    0,
    0,
    dimensions.width,
    dimensions.height,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result);
      } else {
        reject(new Error('Could not encode the converted image'));
      }
    }, 'image/jpeg', JPEG_QUALITY);
  });

  const name = file.name.replace(/\.[^.]+$/, '') || 'image';
  return {
    file: new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified }),
    ...dimensions,
    colorSpace: 'srgb',
  };
};
